/**
 * 이상한 방 - 리빌 증거 표시 (한 건만 뒤집고, 금빛으로 짚는다)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 5절(리빌은 반응을 만드는 연출) ·
 *            `이상한방-상태모델-v0.1.md` 원칙 2(1절) · 3.1절(가려진 위치는 허용)
 *
 * ## 왜 이 파일이 생겼나 - 방 전체를 갈아 끼우면 시선이 흩어진다
 *
 * 리빌은 원본↔변경본 깜빡 교차로 diff를 눈에 띄우는 연출이다(`game/reveal.ts` 참고).
 * 그런데 그 교차를 **방 단위**로 하면 카메라가 한 건에 포커스한 상태에서 화면 구석의
 * 다른 변화 열한 개가 같이 뒤집힌다. 깜빡비교의 원리는 "한 곳만 달라지는 두 장"인데
 * 열두 곳이 동시에 달라지면 시각계가 어느 것도 집어내지 못한다 - 연출이 자기 목적을 지운다.
 * 그래서 이 모듈은 **지금 포커스된 한 건만** 뒤집고 나머지는 변경본 그대로 세워 둔다.
 *
 * ## 씬을 어떻게 건드리는가 - 뷰 트리를 재구성하지 않는다
 *
 * `objectView.syncObjectViews`가 소유한 뷰 컨테이너에 손을 대면 diff 동기화의 전제가 깨진다.
 * 대신 자기 그룹(`reveal-highlight`)을 하나 들고, 원본 쪽 모습은 **그 안에 따로 만든 메시**로
 * 보여 주고 변경본 쪽 실물 뷰는 `visible` 토글로만 숨긴다. 두 메시를 스텝 진입 때 한 번
 * 만들어 두므로 0.35초마다 메시가 생겼다 사라지는 GC 스파이크가 없다.
 *
 * 추가는 변경본에만, 제거는 원본에만 있다. 없는 쪽에서 **아무것도 안 보이는 것이 정답이다** -
 * 없던 것이 나타나고 있던 것이 사라지는 것 자체가 그 변화의 정확한 그림이다.
 * 대신 금빛 브래킷은 물건이 아니라 **자리**에 걸리므로 빈 화면에서도 어디를 보라고 짚는다.
 *
 * ## 금빛은 증거 표시지 파티클이 아니다
 *
 * 색은 `gameHud.css`의 `--gh-accent`(#ffd23f) 하나뿐이다. 표현은 둘로 나뉜다.
 *  1. **모서리 브래킷** - 대상 상자의 여덟 모서리에 갈고리를 건다. 감식 사진의 크롭 마크다.
 *     `depthTest: false`라 가구에 가려도 자리는 보인다(3.1절이 허용한 가려진 배치 때문).
 *  2. **아주 얕은 emissive 숨쉬기** - 물건 자체가 금빛으로 호흡한다.
 *     **진폭이 낮은 것이 실수가 아니다.** 세게 넣으면 색 변경(recolor) 리빌에서 금빛이
 *     원래 색을 덮어 "무엇이 달라졌는가"가 화면에서 사라진다. 그건 원칙 2 위반이다.
 *
 * 리빌은 판마다 변화 개수만큼 반복되는, 이 게임에서 가장 자주 보는 연출이다. 그래서 기준은
 * 화려함이 아니라 **열두 번 연속으로 봐도 견디는가**이고, 진폭은 전부 그 기준에서 나왔다.
 *
 * ## 공유 머티리얼 함정
 *
 * `meshFactory`의 슬롯 머티리얼은 (슬롯 × colorId) 조합당 **하나를 씬 전체가 공유한다.**
 * 거기에 emissive를 쓰면 같은 조합을 쓰는 방 안의 다른 물건이 전부 같이 빛난다 - 한 건만
 * 짚어야 하는 연출이 방 전체 발광이 되고, **안 짚어야 할 물건까지 빛나서 정답을 흘린다.**
 * 그래서 원본 머티리얼을 절대 제자리 변경하지 않고 **복제본으로 참조를 갈아 끼웠다가 정확히
 * 되돌린다**(`applyGlow` / `releaseGlow`). 복제는 `Material.clone()`이라
 * `polygonOffset`(SLOT_DEPTH_BIAS)까지 따라오므로 하이라이트 중에 z-fighting이 되살아나지 않는다.
 */

import {
  Box3,
  BoxGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Vector3,
  type Material,
  type Object3D,
} from 'three'

import { rotatedFootprint } from '../core/grid'
import { clamp01, smoothstep } from '../core/math'
import type { Catalog } from '../core/room'
import type { CatalogEntry, InstanceId, ObjectInstance, RoomState } from '../core/types'
import { isHitCollider } from './meshFactory'
import {
  computeObjectCenterWorld,
  createObjectMesh,
  disposeObject3D,
  footprintToMeters,
  getInstanceIdFromObject3D,
} from './objectView'
import type { SceneCtx } from './scene'

/* --- 상수 ----------------------------------------------------------------------- */

/** 이 모듈이 씬에 붙이는 유일한 노드의 이름. 씬을 덤프했을 때 출처가 바로 읽혀야 한다. */
export const REVEAL_HIGHLIGHT_ROOT_NAME = 'reveal-highlight'

/** 모서리 브래킷 그룹의 이름. 하이라이트 루트의 자식 중 우리가 항상 들고 있는 하나다. */
export const REVEAL_BRACKET_NAME = 'reveal-bracket'

/**
 * 증거 태그의 금색(상수 이름이 `EVIDENCE`인 이유). `ui/gameHud.css`의 `--gh-accent`와 같은 값이다.
 * 2026-09-02에 은유가 방송에서 현장 감식으로 바뀌었어도 이 노랑은 폴리스 라인·표식 태그로
 * 그대로 다시 읽혔으므로 색을 새로 만들 이유가 없었다. 화면에 나가는 말은 아니므로
 * 용어집이 금지한 '증거'가 여기 남아 있어도 된다(금지는 화면 문구에만 걸린다).
 * 여기에 두 번째 색을 추가하면 그때부터 규칙이 흐려진다.
 *
 * 내보내는 이유는 HUD와 같은 값이라는 것이 **테스트로 지켜져야 하는 계약**이기 때문이다.
 */
export const REVEAL_EVIDENCE_GOLD = 0xffd23f

/**
 * 금빛 emissive 세기 상한.
 *
 * 여기를 올리면 색 변경(recolor) 리빌에서 금빛이 원래 색을 덮어 **정답이 화면에서 사라진다.**
 * 그건 난이도가 아니라 원칙 2 위반이라서, 상한 자체를 계약으로 내보내고 테스트가 지킨다.
 */
export const REVEAL_GLOW_MAX_INTENSITY = 0.22

const AXIS_COUNT = 3

/**
 * 여덟 모서리의 부호 조합. 표로 두면 삼중 루프와 인덱스 산수가 사라진다.
 * 순서 자체는 의미가 없다 - 막대 24개가 여덟 모서리에 셋씩 걸리기만 하면 된다.
 */
const CORNER_SIGNS: readonly (readonly number[])[] = [
  [-1, -1, -1],
  [-1, -1, 1],
  [-1, 1, -1],
  [-1, 1, 1],
  [1, -1, -1],
  [1, -1, 1],
  [1, 1, -1],
  [1, 1, 1],
]

/** 브래킷 막대 수 - 여덟 모서리 × 세 축. */
const BRACKET_BAR_COUNT = CORNER_SIGNS.length * AXIS_COUNT

/** 불투명 지오메트리 뒤에 그리게 하는 정렬 순위. `depthTest: false`와 짝이다. */
const BRACKET_RENDER_ORDER = 900

const HALF = 2

/** 브래킷의 모양·움직임. 전부 "열두 번 봐도 견디는가"에서 역산한 값이다. */
const BRACKET = {
  /** 상자 바깥으로 띄우는 여백(해당 축 길이 대비). 딱 붙이면 갈고리가 실루엣을 갉아먹는다. */
  padRatio: 0.07,
  /** 여백 하한(m). 머그컵(9.6cm)처럼 작은 상자에서 비율만 쓰면 여백이 0에 수렴한다. */
  padMinMeters: 0.025,
  /** 갈고리 길이(해당 축 길이 대비)와 하한(m). */
  armRatio: 0.24,
  armMinMeters: 0.05,
  /** 갈고리가 축 길이의 이 비율을 넘으면 브래킷이 아니라 와이어프레임 상자가 된다. */
  armMaxFraction: 0.38,
  /** 막대 두께(상자 최대 변 대비)와 하한(m). 1px 라인은 기기마다 굵기가 달라 쓰지 않는다. */
  thicknessRatio: 0.024,
  thicknessMinMeters: 0.012,
  /** 스텝 진입 스냅인 - 이 배율에서 1로 좁혀 든다. 초점을 맞추는 동작이지 등장 효과가 아니다. */
  snapFromScale: 1.16,
  snapSeconds: 0.18,
  /**
   * 숨쉬기 불투명도 범위. 하한을 0으로 내리지 않는 것이 중요하다 -
   * 완전히 꺼졌다 켜지면 그건 숨쉬기가 아니라 깜빡임이고, 관찰 게임에서 깜빡이는 것은 소음이다.
   */
  opacityMin: 0.46,
  opacityMax: 0.96,
  breathSeconds: 1.2,
} as const

/**
 * 물건 자체의 금빛 숨쉬기(emissive 세기).
 *
 * 상한이 0.22인 이유는 위 파일 주석의 recolor 문제다. 여기를 올리고 싶다면
 * **팔레트에서 가장 가까운 두 색으로 recolor 리빌을 돌려 보고** 올려라.
 *
 * ## 하한이 0.05가 아니라 0.15인 이유 (2026-09-02: 실측 후 상향)
 *
 * 상한만 지키면 될 줄 알았는데 아니었다. 문제는 세기의 **절대값이 아니라 진폭**이다.
 * 깜빡 교차는 "한 곳만 달라지는 두 장"으로 diff를 띄우는 연출인데, 숨쉬기가 크면
 * **물건 전체가 1.2초 주기로 밝아졌다 어두워지면서** 0.35초짜리 교차보다 크고 느린
 * 변화를 만든다. 눈은 크고 느린 쪽을 먼저 잡으므로 정작 봐야 할 한 곳을 놓친다.
 *
 * 실측(단계 7, 세탁기 recolor - 팔레트 0 코코아 → 1 자두, 인접 두 색):
 * 원본↔변경본 두 장의 평균 픽셀차는 0.47(12단계 넘게 달라진 화소 0.11%)인데,
 * **같은 쪽 두 장**을 숨쉬기 위상만 바꿔 비교하면 3.17(10.8%)이었다.
 * 답이 만드는 변화보다 연출이 만드는 변화가 일곱 배 컸다.
 *
 * 그래서 하한을 올려 진폭을 0.17 → 0.07로 줄인다. 금빛 자체는 그대로 있고
 * (개발자가 요청한 "금빛으로 빛나는"은 세기가 아니라 존재로 만족된다),
 * 살아 있다는 신호는 브래킷의 불투명도 숨쉬기가 이미 충분히 낸다.
 * **이 값을 다시 내리려면 위 실측을 다시 돌려라.**
 */
const GLOW = {
  min: 0.15,
  max: REVEAL_GLOW_MAX_INTENSITY,
  breathSeconds: 1.2,
} as const

const TAU = Math.PI * 2

/**
 * 브래킷 배치용 재사용 버퍼.
 *
 * 모듈 전역이어도 안전한 이유는 `layoutBracket`이 **동기적으로 끝나고 값을 즉시 복사**하기 때문이다.
 * 여기서 새로 할당하면 스텝마다 Vector3 두 개 + 배열 두 개가 나오고, 리빌은 판마다 반복된다.
 */
const scratchSize = new Vector3()
const scratchCenter = new Vector3()
const scratchPosition = [0, 0, 0]
const scratchScale = [0, 0, 0]

/* --- 공개 타입 ------------------------------------------------------------------- */

/**
 * 지금 이 한 건을 어느 방의 모습으로 보일지.
 *
 * `game/reveal.ts`의 `RevealView`와 구조가 같은 별개 타입이다 - 렌더가 game을 import 하면
 * 의존이 순환한다(`reveal.ts`가 이미 `render/scene`을 쓴다). 유니온이 동일하므로
 * 호출부는 `RevealView` 값을 변환 없이 그대로 넘기면 된다.
 */
export type RevealSide = 'original' | 'mutated'

export type RevealHighlightOptions = Readonly<{
  ctx: SceneCtx
  catalog: Catalog
  /** 외우기 페이즈의 방. 씬에는 올라가 있지 않고, 포커스된 한 건만 여기서 꺼내 온다. */
  original: RoomState
  /** 술래가 수색하던 방. 씬에 실제로 올라가 있는 쪽이다. */
  mutated: RoomState
  /**
   * 모션을 줄인다. 스냅인·숨쉬기가 사라지고 브래킷과 금빛이 **고정값으로 켜진 채** 남는다.
   * 표시 자체를 끄지 않는 것이 중요하다 - 표시가 없으면 어디를 보라는 정보가 통째로 사라진다.
   */
  reducedMotion?: boolean
}>

export type RevealHighlight = {
  /**
   * 스텝 진입. 이 대상만 뒤집을 수 있게 준비하고 금빛 표시를 켠다.
   * 이전 대상은 자동으로 원상복구된다. 화면은 `'mutated'`에서 시작한다.
   */
  focus(targetId: InstanceId): void
  /** 포커스된 대상만 이 방의 모습으로 바꾼다. 나머지 오브젝트는 변경본 그대로다. */
  setSide(side: RevealSide): void
  /** 프레임 델타(초). 금빛 숨쉬기와 브래킷 스냅인이 여기서 진행된다. */
  update(dt: number): void
  /** 표시를 끄고 씬을 변경본 상태로 정확히 되돌린다. 스텝 사이·리빌 종료 어디서 불러도 안전하다. */
  clear(): void
  /** 수명 종료. 씬에서 자기 노드를 걷어내고 자기가 만든 자원만 해제한다. */
  dispose(): void
}

/* --- 내부 상태 ------------------------------------------------------------------- */

/** 하이라이트가 갈아 끼운 머티리얼 한 건. 되돌릴 때 이 참조를 그대로 다시 꽂는다. */
type MaterialSwap = Readonly<{ mesh: Mesh; original: Material | Material[] }>

/** 한 스텝 동안 붙잡고 있는 것들. `null`이면 포커스 없음이다. */
type FocusState = {
  /** 변경본 쪽 실물 뷰(씬이 소유). 우리가 하는 일은 `visible` 토글뿐이다. */
  liveView: Object3D | null
  /** 원본 쪽 모습(우리가 소유). 원본에 없는 대상이면 `null`. */
  originView: Object3D | null
  /**
   * 두 방에서 완전히 동일한 물건인가. 정상적인 리빌 대상은 언제나 `false`지만,
   * `true`인데도 뒤집으면 **안 바뀐 물건이 원본 쪽에서 사라져 제거로 읽힌다** -
   * 없는 정답을 화면이 만들어 내는 셈이라 여기서 막는다.
   */
  sameInBothRooms: boolean
  /** 각 방에서의 월드 상자. 없는 쪽은 `null`이고 브래킷은 있는 쪽으로 대신 건다. */
  originBox: Box3 | null
  mutatedBox: Box3 | null
  side: RevealSide
  swaps: MaterialSwap[]
  /** 원본 머티리얼 → 발광 복제본. 같은 슬롯을 쓰는 파트가 복제본 하나를 공유한다. */
  clones: Map<Material, MeshStandardMaterial>
  elapsed: number
}

/* --- 생성 ----------------------------------------------------------------------- */

/**
 * 리빌 증거 표시기를 만든다. **라운드당 하나**이며 리빌 페이즈에서만 쓴다.
 *
 * 수색 중에 쓰면 곧바로 정답 유출이다. 이 모듈은 그것을 막을 방법이 없으므로
 * 호출 시점은 전적으로 호출부의 책임이다(`game/session.ts`).
 */
export function createRevealHighlight(options: RevealHighlightOptions): RevealHighlight {
  const { ctx, catalog, original, mutated } = options
  const reducedMotion = options.reducedMotion ?? false

  const root = new Group()
  root.name = REVEAL_HIGHLIGHT_ROOT_NAME
  ctx.scene.add(root)

  // 브래킷 자원은 우리 것이다. `meshFactory`의 공유 유닛 박스를 쓰면 dispose 경로에서
  // "이건 공유물이라 해제하면 안 된다"를 매번 판단해야 하는데, 막대 24개짜리 지오메트리
  // 하나를 직접 갖는 편이 수명이 명확하다.
  const bracketGeometry = new BoxGeometry(1, 1, 1)
  const bracketMaterial = new MeshBasicMaterial({
    color: REVEAL_EVIDENCE_GOLD,
    transparent: true,
    opacity: BRACKET.opacityMax,
    // 가려진 자리에 놓인 변화도 "어디를 보라"까지는 전달해야 한다(3.1절).
    // 몸통은 정상적으로 가려지므로 "가려져 있었다"는 사실 자체는 화면에 남는다.
    depthTest: false,
    depthWrite: false,
    // 톤매핑을 거치면 금색이 HUD의 액센트와 다른 색이 된다. 표시색은 눈금이지 조명이 아니다.
    toneMapped: false,
  })

  const bracket = new Group()
  bracket.name = REVEAL_BRACKET_NAME
  bracket.visible = false
  for (let index = 0; index < BRACKET_BAR_COUNT; index += 1) {
    const bar = new Mesh(bracketGeometry, bracketMaterial)
    bar.renderOrder = BRACKET_RENDER_ORDER
    bar.castShadow = false
    bar.receiveShadow = false
    bracket.add(bar)
  }
  root.add(bracket)

  let focus: FocusState | null = null
  let disposed = false

  /* --- 대상 해석 ---------------------------------------------------------- */

  function entryFor(instance: ObjectInstance): CatalogEntry {
    const entry = catalog.get(instance.catalogId)
    if (entry === undefined) {
      // 조용히 건너뛰면 리빌이 아무것도 안 짚는 화면이 된다(`syncObjectViews`와 같은 태도).
      throw new Error(
        `revealHighlight: 카탈로그에 없는 catalogId '${instance.catalogId}' (인스턴스 '${instance.id}')`,
      )
    }
    return entry
  }

  /* --- 금빛 발광 ---------------------------------------------------------- */

  /** 하위 트리의 머티리얼을 발광 복제본으로 갈아 끼운다. 원본은 건드리지 않는다. */
  function applyGlow(node: Object3D, state: FocusState): void {
    node.traverse((child) => {
      if (!(child instanceof Mesh)) return
      // 보이지 않는 판정 상자까지 빛나게 할 이유가 없다(머티리얼도 발광 대상이 아니다).
      if (isHitCollider(child)) return

      const current = child.material
      const swapped = Array.isArray(current)
        ? current.map((item) => cloneFor(item, state))
        : cloneFor(current, state)
      if (swapped === current) return

      state.swaps.push({ mesh: child, original: current })
      child.material = swapped
    })
  }

  /**
   * 발광 복제본을 얻는다. **같은 원본에는 같은 복제본**을 돌려주므로
   * 슬롯을 공유하는 파트 여럿이 머티리얼 하나를 나눠 쓴다(복제 수가 파트 수가 아니라 슬롯 수다).
   */
  function cloneFor(source: Material, state: FocusState): Material {
    if (!(source instanceof MeshStandardMaterial)) return source

    const cached = state.clones.get(source)
    if (cached !== undefined) return cached

    const created = source.clone()
    created.emissive.setHex(REVEAL_EVIDENCE_GOLD)
    created.emissiveIntensity = reducedMotion ? midpoint(GLOW.min, GLOW.max) : GLOW.min
    state.clones.set(source, created)
    return created
  }

  /** 갈아 끼운 머티리얼을 정확히 되돌리고 복제본을 해제한다. 순서가 뒤집히면 복제본이 샌다. */
  function releaseGlow(state: FocusState): void {
    for (const swap of state.swaps) swap.mesh.material = swap.original
    state.swaps.length = 0

    for (const clone of state.clones.values()) clone.dispose()
    state.clones.clear()
  }

  /* --- 브래킷 ------------------------------------------------------------- */

  /** 지금 보여야 할 상자. 없는 쪽(추가·제거)에서는 있는 쪽 상자를 그대로 쓴다. */
  function boxForSide(state: FocusState): Box3 | null {
    const preferred = state.side === 'original' ? state.originBox : state.mutatedBox
    return preferred ?? state.originBox ?? state.mutatedBox
  }

  /**
   * 막대 24개를 상자에 맞춰 다시 놓는다.
   *
   * 막대는 **상자 중심 기준 로컬 좌표**에 놓고 그룹을 월드 중심으로 옮긴다.
   * 그래야 스냅인을 `bracket.scale` 하나로 끝낼 수 있다 - 막대마다 좌표를 다시 계산하면
   * 애니메이션 프레임마다 24번의 산수를 도는 셈이 된다.
   */
  function layoutBracket(state: FocusState): void {
    const box = boxForSide(state)
    if (box === null) {
      bracket.visible = false
      return
    }

    const size = box.getSize(scratchSize)
    const axes = [size.x, size.y, size.z]
    const thickness = Math.max(
      Math.max(size.x, size.y, size.z) * BRACKET.thicknessRatio,
      BRACKET.thicknessMinMeters,
    )

    // 축별로 반 길이와 갈고리 길이를 따로 낸다. 하나로 묶으면 납작한 물건(러그·액자)에서
    // 갈고리가 물건을 통째로 감싸 브래킷이 아니라 상자가 된다.
    const half = axes.map(
      (axis) => axis / HALF + Math.max(axis * BRACKET.padRatio, BRACKET.padMinMeters),
    )
    const arm = half.map((value) => {
      const full = value * HALF
      return Math.min(
        Math.max(full * BRACKET.armRatio, BRACKET.armMinMeters),
        full * BRACKET.armMaxFraction,
      )
    })

    bracket.position.copy(box.getCenter(scratchCenter))
    bracket.visible = true

    CORNER_SIGNS.forEach((signs, corner) => {
      for (let bent = 0; bent < AXIS_COUNT; bent += 1) {
        const bar = bracket.children[corner * AXIS_COUNT + bent]
        if (!(bar instanceof Mesh)) continue

        const length = arm[bent] ?? 0
        for (let axis = 0; axis < AXIS_COUNT; axis += 1) {
          const sign = signs[axis] ?? 1
          // 갈고리는 모서리에서 **안쪽으로** 뻗는다. 중심을 반 길이만큼 당겨야 끝이 모서리에 닿는다.
          scratchPosition[axis] = sign * (half[axis] ?? 0) - (axis === bent ? sign * (length / HALF) : 0)
          scratchScale[axis] = axis === bent ? length : thickness
        }

        bar.position.fromArray(scratchPosition)
        bar.scale.fromArray(scratchScale)
      }
    })
  }

  /* --- 스텝 제어 ---------------------------------------------------------- */

  function clear(): void {
    if (focus === null) return

    releaseGlow(focus)

    // 실물 뷰는 씬의 것이다. 우리가 끈 것만 정확히 되돌린다.
    if (focus.liveView !== null) focus.liveView.visible = true

    if (focus.originView !== null) {
      root.remove(focus.originView)
      // 머티리얼을 되돌린 **뒤에** 해제해야 한다 - 복제본이 꽂힌 채로 부르면
      // `disposeObject3D`가 공유 표식을 보고 건너뛰어서 복제본이 그대로 샌다.
      disposeObject3D(focus.originView)
    }

    bracket.visible = false
    focus = null
  }

  function focusOn(targetId: InstanceId): void {
    if (disposed) return
    clear()

    const originInstance = original.objects.get(targetId)
    const mutatedInstance = mutated.objects.get(targetId)
    if (originInstance === undefined && mutatedInstance === undefined) {
      // 두 방 어디에도 없는 id는 리빌 시퀀스가 만들어질 수 없는 값이다. 조용히 넘기면
      // "카메라만 움직이고 아무 일도 안 일어나는 스텝"이 되어 원인 추적이 불가능해진다.
      throw new Error(`revealHighlight: 원본·변경본 어디에도 없는 대상 '${targetId}'`)
    }

    const state: FocusState = {
      liveView: null,
      originView: null,
      sameInBothRooms: originInstance !== undefined && originInstance === mutatedInstance,
      originBox: null,
      mutatedBox: null,
      side: 'mutated',
      swaps: [],
      clones: new Map(),
      elapsed: 0,
    }

    if (mutatedInstance !== undefined) {
      state.liveView = findLiveView(ctx.scene, targetId, root)
      state.mutatedBox =
        state.liveView === null
          ? footprintBox(mutatedInstance, entryFor(mutatedInstance))
          : visibleWorldBox(state.liveView, mutatedInstance, entryFor(mutatedInstance))
    }

    // 원본 쪽 모습은 **변경본과 실제로 다를 때만** 따로 만든다. 코어 상태가 불변이라
    // 안 바뀐 오브젝트는 두 방이 참조를 공유하므로, 참조 비교 한 번이면 판정이 끝난다.
    if (originInstance !== undefined && originInstance !== mutatedInstance) {
      const entry = entryFor(originInstance)
      const view = createObjectMesh(originInstance, entry)
      view.visible = false
      root.add(view)
      state.originView = view
      state.originBox = visibleWorldBox(view, originInstance, entry)
    } else if (originInstance !== undefined) {
      state.originBox = state.mutatedBox
    }

    // 발광은 양쪽 메시에 미리 걸어 둔다. 깜빡일 때마다 걸었다 풀면 0.35초마다
    // 머티리얼 복제·해제가 돌아 리빌 내내 GC가 튄다.
    if (state.liveView !== null) applyGlow(state.liveView, state)
    if (state.originView !== null) applyGlow(state.originView, state)

    focus = state
    // `applySide`가 브래킷 배치까지 한다. 시간 값(스냅인·숨쉬기)은 첫 프레임 전에도
    // 올바른 시작값이어야 하므로 여기서 한 번 찍어 둔다 - 안 그러면 진입 순간 한 프레임이 튄다.
    applySide('mutated')
    applyAnimation(state)
  }

  function applySide(side: RevealSide): void {
    if (focus === null) return
    focus.side = side

    const showOrigin = side === 'original'
    // 원본에 없는 대상(추가)이면 실물 뷰를 숨긴 채 아무것도 보여 주지 않는 것이 정답이다.
    // 반대로 두 방에서 동일한 물건은 뒤집을 것이 없으므로 손대지 않는다(`sameInBothRooms` 주석).
    if (focus.liveView !== null && !focus.sameInBothRooms) {
      focus.liveView.visible = !showOrigin
    }
    if (focus.originView !== null) focus.originView.visible = showOrigin

    layoutBracket(focus)
  }

  /** 시간에 따른 값만 반영한다. 배치(브래킷 좌표)는 `layoutBracket`이 따로 한다. */
  function applyAnimation(state: FocusState): void {
    if (reducedMotion) {
      bracket.scale.setScalar(1)
      bracketMaterial.opacity = midpoint(BRACKET.opacityMin, BRACKET.opacityMax)
      for (const clone of state.clones.values()) {
        clone.emissiveIntensity = midpoint(GLOW.min, GLOW.max)
      }
      return
    }

    const snap = BRACKET.snapSeconds <= 0 ? 1 : clamp01(state.elapsed / BRACKET.snapSeconds)
    const eased = smoothstep(snap)
    bracket.scale.setScalar(BRACKET.snapFromScale + (1 - BRACKET.snapFromScale) * eased)

    const beat = breathe(state.elapsed, BRACKET.breathSeconds)
    bracketMaterial.opacity =
      lerp(BRACKET.opacityMin, BRACKET.opacityMax, beat) * eased

    const glow = lerp(GLOW.min, GLOW.max, breathe(state.elapsed, GLOW.breathSeconds))
    for (const clone of state.clones.values()) clone.emissiveIntensity = glow
  }

  return {
    focus: focusOn,

    setSide(side: RevealSide): void {
      if (disposed || focus === null) return
      if (focus.side === side) return
      applySide(side)
    },

    update(dt: number): void {
      if (disposed || focus === null) return
      focus.elapsed += Math.max(dt, 0)
      applyAnimation(focus)
    },

    clear,

    dispose(): void {
      if (disposed) return
      clear()
      disposed = true

      root.removeFromParent()
      root.clear()
      // 브래킷 자원은 이 모듈이 직접 만든 것뿐이다. 공유 자원(슬롯 머티리얼·유닛 지오메트리)은
      // 여기서 절대 건드리지 않는다 - `disposeMeshFactory()`의 몫이다.
      bracketGeometry.dispose()
      bracketMaterial.dispose()
    },
  }
}

/* --- 순수 도우미 ---------------------------------------------------------------- */

function lerp(from: number, to: number, ratio: number): number {
  return from + (to - from) * ratio
}

/** 모션을 끈 환경에서 쓰는 고정값. 진동의 중간이라 "가장 오래 보이던 밝기"와 같다. */
function midpoint(from: number, to: number): number {
  return (from + to) / HALF
}

/** 0 → 1 → 0을 부드럽게 오가는 숨쉬기. 코사인이라 양 끝에서 멈춰 서 깜빡임으로 읽히지 않는다. */
function breathe(elapsed: number, period: number): number {
  if (period <= 0) return 1
  return 0.5 - 0.5 * Math.cos((elapsed / period) * TAU)
}

/**
 * 씬에 올라가 있는 오브젝트 뷰의 루트를 찾는다.
 *
 * 이름이 아니라 `getInstanceIdFromObject3D`로 찾는 이유: 뷰 루트의 이름 규칙은
 * `objectView.ts`의 내부 사정이고, 여기서 문자열을 복제하면 저쪽이 이름을 바꾸는 순간
 * **조용히 아무것도 못 찾는** 상태가 된다. 그 함수는 부모를 거슬러 올라가므로
 * 깊이 우선으로 처음 걸리는 노드가 곧 뷰 루트다(자식보다 부모를 먼저 방문한다).
 *
 * `skip`은 이 모듈 자신의 그룹이다 - 우리가 만든 원본 메시도 같은 id를 들고 있어서
 * 걸러 내지 않으면 자기 것을 실물 뷰로 착각한다.
 */
function findLiveView(node: Object3D, targetId: InstanceId, skip: Object3D): Object3D | null {
  for (const child of node.children) {
    if (child === skip) continue
    if (getInstanceIdFromObject3D(child) === targetId) return child

    const found = findLiveView(child, targetId, skip)
    if (found !== null) return found
  }

  return null
}

/**
 * 보이는 부품만의 월드 바운딩 상자.
 *
 * 판정 상자를 빼는 이유는 그것이 언제나 점유 셀 크기라서다. 그대로 재면 머그컵(9.6cm)의
 * 브래킷이 25cm 셀 경계에 걸려 **아무것에도 붙지 않은 금색 틀**이 뜬다.
 * 보이는 것이 하나도 없으면(있을 수 없지만) 점유 상자로 물러난다 - 빈 상자를 그대로 쓰면
 * 브래킷이 월드 원점의 한 점으로 붕괴한다.
 */
function visibleWorldBox(view: Object3D, instance: ObjectInstance, entry: CatalogEntry): Box3 {
  const box = new Box3()
  view.updateMatrixWorld(true)

  view.traverse((child) => {
    if (!(child instanceof Mesh)) return
    if (isHitCollider(child)) return
    box.expandByObject(child)
  })

  return box.isEmpty() ? footprintBox(instance, entry) : box
}

/** 점유 셀 그대로의 월드 상자. 회전을 반영한 크기를 쓰므로 화면과 점유가 어긋나지 않는다. */
function footprintBox(instance: ObjectInstance, entry: CatalogEntry): Box3 {
  const center = computeObjectCenterWorld(instance.cell, entry, instance.rot)
  const size = footprintToMeters(rotatedFootprint(entry.footprint, instance.rot))

  return new Box3(
    new Vector3(center.x - size.x / HALF, center.y - size.y / HALF, center.z - size.z / HALF),
    new Vector3(center.x + size.x / HALF, center.y + size.y / HALF, center.z + size.z / HALF),
  )
}
